import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { ChevronRight, Megaphone } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Pagination } from '@/components/common/Pagination';
import { EmptyState } from '@/components/common/EmptyState';
import { useRcsCampaignsList } from '@/hooks/useRcs';
import type { RcsCampaignStatus } from '@/types/api.types';

const PAGE_SIZE = 15;

const STATUS_LABEL: Record<RcsCampaignStatus, string> = {
  draft: 'Rascunho',
  scheduled: 'Agendada',
  running: 'Em andamento',
  completed: 'Concluída',
  failed: 'Falhou',
  cancelled: 'Cancelada',
};

const STATUS_VARIANT: Record<
  RcsCampaignStatus,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  draft: 'secondary',
  scheduled: 'outline',
  running: 'default',
  completed: 'secondary',
  failed: 'destructive',
  cancelled: 'destructive',
};

function RcsCampaignListSkeleton() {
  return (
    <div className="space-y-3 p-1">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex items-center gap-4">
          <Skeleton className="h-5 flex-1" />
          <Skeleton className="h-5 w-24" />
          <Skeleton className="h-5 w-16" />
          <Skeleton className="h-5 w-20" />
          <Skeleton className="h-8 w-8" />
        </div>
      ))}
    </div>
  );
}

export function RcsCampaignList() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const { data, isLoading } = useRcsCampaignsList({ page, pageSize: PAGE_SIZE });
  const campaigns = data?.data ?? [];
  const meta = data?.meta;

  if (isLoading) return <RcsCampaignListSkeleton />;

  if (campaigns.length === 0) {
    return (
      <EmptyState
        icon={Megaphone}
        title="Nenhuma campanha RCS ainda"
        description="Crie sua primeira campanha para começar a disparar mensagens RCS."
      />
    );
  }

  return (
    <div className="space-y-3">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Nome</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Destinatários</TableHead>
            <TableHead className="text-right">Enviados</TableHead>
            <TableHead className="text-right">Falhas</TableHead>
            <TableHead>Criada</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {campaigns.map((c) => (
            <TableRow
              key={c.id}
              className="cursor-pointer"
              onClick={() => navigate(`/rcs/campaigns/${c.id}`)}
            >
              <TableCell className="font-medium">{c.name}</TableCell>
              <TableCell>
                <Badge variant={STATUS_VARIANT[c.status]}>{STATUS_LABEL[c.status]}</Badge>
              </TableCell>
              <TableCell className="text-right">{c.totalRecipients}</TableCell>
              <TableCell className="text-right">{c.totalSent}</TableCell>
              <TableCell className="text-right text-destructive">{c.totalFailed}</TableCell>
              <TableCell className="text-xs text-muted-foreground">
                {format(new Date(c.createdAt), 'dd/MM/yyyy', { locale: ptBR })}
              </TableCell>
              <TableCell>
                <Button variant="ghost" size="icon">
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {meta && (
        <Pagination
          page={page}
          totalPages={meta.totalPages}
          total={meta.total}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
        />
      )}
    </div>
  );
}
